import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axios';
import { Search, Calendar, ChevronDown, X, ChevronLeft, ChevronRight } from 'lucide-react';
import formatCurrency from '../helper/formatCurrency';
import CustomLoader from './CustomLoader';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';

interface DailyReportItem {
  date: string;
  new_users: number;
  active_users: number;
  total_deposit: number;
  total_withdrawal: number;
  total_refer_in: number;
  cashback: number;
  win_loss: number;
}

interface AgentItem {
  id: number;
  username: string;
}

const DailyReport: React.FC = () => {
  const { user } = useAuth();
  const [dataList, setDataList] = useState<DailyReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Custom Month Picker state
  const getInitialMonthString = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${mm}`;
  };

  const [selectedMonth, setSelectedMonth] = useState<string>(getInitialMonthString()); // YYYY-MM
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  // Agent Selection state
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentItem | null>(null);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target as Node)) {
        setMonthPickerOpen(false);
      }
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target as Node)) {
        setAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch agents list
  const fetchAgents = async () => {
    try {
      const res = await api.get('/agent/list');
      if (res.data && res.data.data) {
        setAgents(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching agents list:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'backoffice') {
      fetchAgents();
    }
  }, [user]);

  const fetchDailyReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/daily-report', {
        params: {
          month: selectedMonth,
          agentId: selectedAgent?.id || undefined,
        },
      });

      if (res.data.status) {
        setDataList(res.data.data);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch daily report details.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyReport();
  }, [selectedMonth, selectedAgent]);

  const getMonthName = (monthString: string) => {
    const [year, month] = monthString.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleSelectMonth = (monthIndex: number) => {
    const mm = String(monthIndex + 1).padStart(2, '0');
    setSelectedMonth(`${pickerYear}-${mm}`);
    setMonthPickerOpen(false);
  };

  // Totals calculations
  const calculateTotals = () => {
    let newUsers = 0;
    let activeUsers = 0;
    let deposit = 0;
    let withdrawal = 0;
    let referIn = 0;
    let cashback = 0;
    let winLoss = 0;

    dataList.forEach((item) => {
      newUsers += Number(item.new_users || 0);
      activeUsers += Number(item.active_users || 0);
      deposit += Number(item.total_deposit || 0);
      withdrawal += Number(item.total_withdrawal || 0);
      referIn += Number(item.total_refer_in || 0);
      cashback += Number(item.cashback || 0);
      winLoss += Number(item.win_loss || 0);
    });

    return {
      newUsers,
      activeUsers,
      deposit,
      withdrawal,
      referIn,
      cashback,
      winLoss,
    };
  };

  const totals = calculateTotals();

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const filteredAgents = agents.filter(agent => 
    agent.username.toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Daily Report</h1>
          <p className="text-sm text-gray-500">Home / Daily Report</p>
        </div>
      </div>

      {/* Filter Options */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl border shadow-sm">
        {/* Month Picker */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-600">Month</span>
          <div className="relative" ref={monthPickerRef}>
            <button
              onClick={() => setMonthPickerOpen(!monthPickerOpen)}
              className="flex items-center gap-2 px-3 py-1.5 border rounded bg-white text-sm font-medium hover:bg-gray-50 transition"
            >
              <Calendar className="w-4 h-4 text-gray-500" />
              {getMonthName(selectedMonth)}
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {monthPickerOpen && (
              <div className="absolute left-0 mt-1.5 w-64 bg-white border rounded-lg shadow-xl z-50 p-3 space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <button 
                    onClick={() => setPickerYear(y => y - 1)}
                    className="p-1 hover:bg-gray-100 rounded transition"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-sm text-gray-700">{pickerYear}</span>
                  <button 
                    onClick={() => setPickerYear(y => y + 1)}
                    className="p-1 hover:bg-gray-100 rounded transition"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {months.map((m, idx) => (
                    <button
                      key={m}
                      onClick={() => handleSelectMonth(idx)}
                      className={`py-1.5 text-xs font-semibold rounded hover:bg-blue-50 transition ${
                        selectedMonth === `${pickerYear}-${String(idx + 1).padStart(2, '0')}`
                          ? 'bg-blue-600 text-white hover:bg-blue-600'
                          : 'text-gray-700'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t text-xs">
                  <button
                    onClick={() => {
                      setSelectedMonth(getInitialMonthString());
                      setMonthPickerOpen(false);
                    }}
                    className="text-blue-600 font-semibold hover:underline"
                  >
                    This month
                  </button>
                  <button
                    onClick={() => setMonthPickerOpen(false)}
                    className="text-gray-500 hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Agent Selector (visible only for admin/backoffice) */}
        {(user?.role === 'admin' || user?.role === 'backoffice') && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-600">Agent</span>
            <div className="relative w-48" ref={agentDropdownRef}>
              <button
                onClick={() => setAgentDropdownOpen(!agentDropdownOpen)}
                className="flex justify-between items-center w-full px-3 py-1.5 border rounded bg-white text-sm font-medium hover:bg-gray-50 transition"
              >
                <span className="truncate">{selectedAgent ? selectedAgent.username : 'Select Agent'}</span>
                <div className="flex items-center gap-1">
                  {selectedAgent && (
                    <X
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAgent(null);
                      }}
                      className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                    />
                  )}
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </div>
              </button>

              {agentDropdownOpen && (
                <div className="absolute left-0 mt-1.5 w-full bg-white border rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredAgents.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-500">No agents found</div>
                    ) : (
                      filteredAgents.map((agent) => (
                        <button
                          key={agent.id}
                          onClick={() => {
                            setSelectedAgent(agent);
                            setAgentDropdownOpen(false);
                            setAgentSearch('');
                          }}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition ${
                            selectedAgent?.id === agent.id ? 'bg-blue-50 font-bold text-blue-600' : 'text-gray-700'
                          }`}
                        >
                          {agent.username}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-50 border-b">
            <tr>
              {[
                'Date',
                'New User',
                'Active User',
                'Total deposit',
                'Total Withdrawal',
                'Total Refer In',
                'Cash Back',
                'Win/Loss',
              ].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-r last:border-r-0">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="flex justify-center items-center">
                    <CustomLoader />
                  </div>
                </td>
              </tr>
            ) : dataList.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-gray-500 font-semibold">
                  No records found.
                </td>
              </tr>
            ) : (
              dataList.map((item) => (
                <tr key={item.date} className="hover:bg-gray-50/50 transition">
                  <td className="px-6 py-4 text-gray-900 font-medium border-r">{item.date}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{item.new_users}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{item.active_users}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{formatCurrency(Number(item.total_deposit))}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{formatCurrency(Number(item.total_withdrawal))}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{formatCurrency(Number(item.total_refer_in))}</td>
                  <td className="px-6 py-4 text-gray-900 border-r">{formatCurrency(Number(item.cashback))}</td>
                  <td className={`px-6 py-4 font-semibold border-r last:border-r-0 ${
                    Number(item.win_loss) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(Number(item.win_loss))}
                  </td>
                </tr>
              ))
            )}
          </tbody>

          {!loading && dataList.length > 0 && (
            <tfoot className="bg-gray-50 border-t font-bold text-gray-900">
              <tr>
                <td className="px-6 py-4 border-r">Total</td>
                <td className="px-6 py-4 border-r">{totals.newUsers}</td>
                <td className="px-6 py-4 border-r">{totals.activeUsers}</td>
                <td className="px-6 py-4 border-r">{formatCurrency(totals.deposit)}</td>
                <td className="px-6 py-4 border-r">{formatCurrency(totals.withdrawal)}</td>
                <td className="px-6 py-4 border-r">{formatCurrency(totals.referIn)}</td>
                <td className="px-6 py-4 border-r">{formatCurrency(totals.cashback)}</td>
                <td className={`px-6 py-4 border-r last:border-r-0 ${
                  totals.winLoss >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(totals.winLoss)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default DailyReport;
